/**
 * About — anonymous-friendly company page for Factskoot Eduwellness Pvt. Ltd.
 * Routes: /about
 */
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import {
  Building2, Sparkles, Heart, Globe, Shield, Award, ArrowRight,
  Users, BookOpen, Mail, ExternalLink, Compass,
} from "lucide-react";

export default function About() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  return (
    <Layout title="About TeeBee" anonymous={!user && !authLoading}>
      {/* Hero */}
      <section className="tb-card-feature relative overflow-hidden" data-testid="about-hero">
        <div className="absolute inset-0 tb-grid-bg opacity-40 pointer-events-none" />
        <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-gradient-to-br from-amber-200/40 to-violet-200/30 blur-3xl pointer-events-none" />
        <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
          <div className="lg:col-span-7">
            <span className="tb-chip-lavender inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide">
              <Building2 className="h-3 w-3" /> About TeeBee
            </span>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight font-medium leading-tight">
              An AI Global OS for educators, built by educators.
            </h1>
            <p className="mt-4 text-base text-muted-foreground leading-relaxed max-w-xl">
              TeeBee is the AI co-teacher and educator marketplace from{" "}
              <span className="font-medium text-foreground">Factskoot Eduwellness Pvt. Ltd.</span> — a company on a mission to give the world&apos;s
              30 million teachers more time, better tools, and a global stage for their craft.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              <Button onClick={() => navigate(user ? "/dashboard" : "/signup")} className="rounded-full h-11 px-5" data-testid="about-cta-primary">
                {user ? "Open TeeBee" : "Start free"} <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
              <a href="https://www.factskoot.com/" target="_blank" rel="noopener noreferrer" data-testid="about-factskoot-link">
                <Button variant="outline" className="rounded-full h-11 px-5">
                  Visit Factskoot.com <ExternalLink className="h-4 w-4 ml-1.5" />
                </Button>
              </a>
            </div>
          </div>
          <div className="lg:col-span-5 flex justify-center items-center">
            {/* LIGHT mode — full vertical lockup */}
            <span className="relative dark:hidden inline-block">
              <img
                src="/teebee-logo-vertical.png?v=11"
                alt="TeeBee — by Factskoot"
                loading="lazy"
                decoding="async"
                className="w-full max-w-[320px] h-auto tb-logo-art"
                data-testid="about-logo-art"
              />
              <span
                aria-hidden
                className="tb-logo-tm absolute select-none pointer-events-none"
                style={{ top: "60%", left: "80%" }}
              >™</span>
            </span>
            {/* DARK mode — clean icon + HTML wordmark */}
            <div className="hidden dark:flex flex-col items-center text-center max-w-xs">
              <span className="relative">
                <span className="absolute inset-0 rounded-full bg-amber-400/30 blur-3xl pointer-events-none" />
                <span className="absolute inset-0 rounded-full bg-violet-400/25 blur-2xl pointer-events-none" />
                <img
                  src="/icon-512.png?v=11"
                  alt="TeeBee"
                  loading="lazy"
                  decoding="async"
                  className="relative h-32 w-32 sm:h-36 sm:w-36 object-contain tb-logo-art"
                  draggable={false}
                />
              </span>
              <p className="mt-3 font-display text-4xl tracking-tight font-medium leading-none">
                <span className="text-foreground">Tee</span>
                <span className="bg-gradient-to-r from-violet-300 to-fuchsia-300 bg-clip-text text-transparent">Bee</span>
                <span
                  aria-hidden
                  className="tb-logo-tm tb-logo-tm-sm relative inline-block align-super -top-2"
                  style={{ marginLeft: "2px" }}
                >™</span>
              </p>
              <p className="mt-1 text-[10px] uppercase tracking-[0.3em] text-muted-foreground font-medium">by Factskoot</p>
              <p className="mt-2 text-xs sm:text-sm text-muted-foreground">Global AI Powered OS &amp; Marketplace for Educators</p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission + Vision */}
      <section className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-5 tb-stagger">
        <div className="tb-card p-6" data-testid="about-mission">
          <span className="h-10 w-10 rounded-2xl tb-chip-lavender grid place-items-center"><Heart className="h-5 w-5" /></span>
          <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground mt-3">Our mission</p>
          <h2 className="mt-1 font-display text-2xl tracking-tight font-medium">Give every teacher back their evenings.</h2>
          <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
            Lesson plans, OCR scanning, grading, attendance, CV writing, hiring conversations — TeeBee unifies the
            entire teaching workflow into one AI-native operating system. So teachers can focus on what only humans
            can do: inspire.
          </p>
        </div>
        <div className="tb-card p-6" data-testid="about-vision">
          <span className="h-10 w-10 rounded-2xl tb-chip-mint grid place-items-center"><Globe className="h-5 w-5" /></span>
          <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground mt-3">Our vision</p>
          <h2 className="mt-1 font-display text-2xl tracking-tight font-medium">The global operating system for educators.</h2>
          <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
            From a remote classroom in Bengaluru to a faculty room in Boston — TeeBee gives every educator the same
            premium AI co-teacher, public portfolio, and global hiring network.
          </p>
        </div>
      </section>

      {/* Parent company section */}
      <section className="mt-8 tb-card-feature relative overflow-hidden" data-testid="about-parent">
        <div className="absolute inset-0 tb-grid-bg opacity-30 pointer-events-none" />
        <div className="relative grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-7">
            <span className="tb-chip-sky inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide">
              <Building2 className="h-3 w-3" /> Parent Company
            </span>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl tracking-tight font-medium">
              Factskoot Eduwellness Private Limited
            </h2>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed max-w-2xl">
              Factskoot Eduwellness Pvt. Ltd. is the parent company behind TeeBee. Headquartered in India and serving
              educators worldwide, Factskoot is dedicated to advancing teacher wellness, professional development,
              and AI-powered classroom innovation.
            </p>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed max-w-2xl">
              The Factskoot mission is to build practical, dignified, and globally-relevant tools that put educators
              first — combining education, technology, and wellness into a single trusted ecosystem.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <a href="https://www.factskoot.com/" target="_blank" rel="noopener noreferrer">
                <Button className="rounded-full" data-testid="about-visit-factskoot">
                  <Globe className="h-3.5 w-3.5 mr-1.5" /> factskoot.com <ExternalLink className="h-3.5 w-3.5 ml-1.5" />
                </Button>
              </a>
              <a href="mailto:hello@factskoot.com">
                <Button variant="outline" className="rounded-full" data-testid="about-email">
                  <Mail className="h-3.5 w-3.5 mr-1.5" /> hello@factskoot.com
                </Button>
              </a>
            </div>
          </div>
          <div className="md:col-span-5 grid grid-cols-2 gap-3 content-start">
            <Fact label="Registered name" value="Factskoot Eduwellness Pvt. Ltd." />
            <Fact label="Headquarters"     value="India" />
            <Fact label="Flagship product" value="TeeBee" />
            <Fact label="Founded for"      value="Teacher wellness + AI" />
          </div>
        </div>
      </section>

      {/* What TeeBee is */}
      <section className="mt-8" data-testid="about-pillars">
        <div className="mb-5">
          <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground">What TeeBee is</p>
          <h2 className="mt-2 font-display text-2xl sm:text-3xl tracking-tight font-medium">Nine pillars · one AI Global OS.</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 tb-stagger">
          <Pillar icon={Sparkles}  chip="tb-chip-lavender" title="AI Teacher Companion"             body="Plan, draft, scan, grade." />
          <Pillar icon={BookOpen}  chip="tb-chip-sky"      title="AI Lesson Workspace"              body="Unified canvas for every lesson." />
          <Pillar icon={Award}     chip="tb-chip-butter"   title="OCR + Teach From Anything"        body="Photo to ready-to-teach." />
          <Pillar icon={Users}     chip="tb-chip-mint"     title="Educator Profile & CV Builder"    body="A premium identity in minutes." />
          <Pillar icon={Compass}   chip="tb-chip-rose"     title="Career Copilot"                   body="Plan, score, prep, win." />
          <Pillar icon={Globe}     chip="tb-chip-lavender" title="Public Lesson Network"            body="Share, remix, get discovered." />
          <Pillar icon={Building2} chip="tb-chip-sky"      title="Educator Marketplace"             body="Discoverable on your terms." />
          <Pillar icon={Shield}    chip="tb-chip-mint"     title="Hiring Network for Schools"        body="Privacy-first matching." />
          <Pillar icon={Sparkles}  chip="tb-chip-butter"   title="TeeBee Coins Growth Engine"        body="Refer, earn, recharge, boost." />
        </div>
      </section>

      {/* Values */}
      <section className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 tb-stagger" data-testid="about-values">
        <Value icon={Shield} title="Privacy by design"
               body="Teachers control every field recruiters see. Defaults are locked. Trust is non-negotiable." />
        <Value icon={Globe}  title="Globally relevant, locally rooted"
               body="Built for educators in India and around the world — across CBSE, ICSE, IB, Cambridge, and university faculties." />
        <Value icon={Heart}  title="Educator dignity first"
               body="No clutter. No dark patterns. No vendor branding. Just elegant tools that respect a teacher's time." />
      </section>

      {/* Final CTA */}
      <section className="mt-10 tb-card-feature relative overflow-hidden text-center">
        <div className="absolute inset-0 tb-grid-bg opacity-40 pointer-events-none" />
        <div className="relative max-w-2xl mx-auto">
          <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground">Join the movement</p>
          <h2 className="mt-2 font-display text-3xl tracking-tight font-medium">Build TeeBee with us.</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Whether you teach, hire, build curriculum, or invest in education — there&apos;s a place for you on TeeBee.
          </p>
          <div className="mt-4 flex flex-wrap gap-2 justify-center">
            <Button onClick={() => navigate(user ? "/dashboard" : "/signup")} className="rounded-full h-11 px-5">
              {user ? "Back to TeeBee" : "Create free account"} <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
            <Button variant="outline" onClick={() => navigate("/marketplace")} className="rounded-full h-11 px-5">
              Visit the Marketplace
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
}

function Fact({ label, value }) {
  return (
    <div className="rounded-2xl border border-border bg-card/60 p-3">
      <p className="text-[9px] uppercase tracking-[0.22em] font-semibold text-muted-foreground">{label}</p>
      <p className="text-sm font-medium tracking-tight mt-1 leading-snug">{value}</p>
    </div>
  );
}

function Pillar({ icon: Icon, chip, title, body }) {
  return (
    <div className="tb-card p-4">
      <span className={`inline-grid place-items-center h-9 w-9 rounded-xl ${chip}`}>
        <Icon className="h-4 w-4" />
      </span>
      <p className="mt-3 text-sm font-display font-medium tracking-tight leading-snug">{title}</p>
      <p className="mt-1 text-[11px] text-muted-foreground">{body}</p>
    </div>
  );
}

function Value({ icon: Icon, title, body }) {
  return (
    <div className="tb-card p-5">
      <span className="h-9 w-9 rounded-xl tb-chip-lavender grid place-items-center">
        <Icon className="h-4 w-4" />
      </span>
      <p className="font-display text-base font-medium mt-3">{title}</p>
      <p className="text-[12px] text-muted-foreground mt-1 leading-relaxed">{body}</p>
    </div>
  );
}
